// src/features/invoices/InvoiceAcceptanceState.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    Tooltip,
} from '@mui/material';

// --- Step-Konfiguration ----------------------------------------------------

const getSteps = (oldSystem, labelMode = 'internal') => {
    if (oldSystem) {
        return ['Invoice correct', 'AM approved', 'Olga approved', 'Paid'];
    }
    if (labelMode === 'creator') {
        return ['Upload', 'Approval', 'Payment', 'Paid'];
    }
    return ['Uploaded', 'Approved', 'Ready', 'Paid'];
};

const oldStepKeys = [
    'stepOneCorrect',
    'stepTwoAmApproved',
    'stepThreeAdminApproved',
    'stepFourPaid',
];

const stepKeys = [
    'stepOneUpload',
    'stepTwoApproved',
    'stepThreeReady',
    'stepFourPaid',
];

const getStepContent = (step, active, rejected, done, oldSystem, labelMode = 'internal') => {
    if (oldSystem) {
        switch (step) {
            case 0:
                return rejected
                    ? 'Invoice incorrect'
                    : active || !done
                        ? 'Waiting for Finance approval'
                        : 'Finance approved';
            case 1:
                return rejected
                    ? 'Rejected'
                    : active || !done
                        ? 'Waiting for AM approval'
                        : 'AM approved';
            case 2:
                return rejected
                    ? 'Rejected'
                    : active || !done
                        ? 'Waiting for admin approval'
                        : 'Admin approved';
            default:
                return 'Paid';
        }
    }

    // neues System
    if (labelMode === 'creator') {
        switch (step) {
            case 0:
                return rejected
                    ? 'Needs changes'
                    : active || !done
                        ? 'Waiting for structural review'
                        : 'Checked';
            case 1:
                return rejected
                    ? 'Rejected'
                    : active || !done
                        ? 'Waiting for approval'
                        : 'Approved';
            case 2:
                return rejected
                    ? 'Rejected'
                    : active || !done
                        ? 'Waiting for payment release'
                        : 'Waiting for payment';
            default:
                return active ? 'Waiting for payment' : 'Paid';
        }
    }

    switch (step) {
        case 0:
            return rejected
                ? 'Invoice incorrect'
                : active || !done
                    ? 'Waiting for Finance approval'
                    : 'Finance approved';
        case 1:
            return rejected
                ? 'Rejected'
                : active || !done
                    ? 'Waiting for admin approval'
                    : 'Admin approved';
        case 2:
            return rejected
                ? 'Rejected'
                : active || !done
                    ? 'To be paid?'
                    : 'Pay now';
        default:
            return 'Paid';
    }
};

const getTooltipText = (step, active, rejected, labelMode = 'internal') => {
    if (labelMode === 'creator') {
        switch (step) {
            case 0:
                return rejected
                    ? 'Please update invoice details'
                    : active
                        ? 'Finance checks invoice structure'
                        : 'Structure confirmed';
            case 1:
                return rejected
                    ? 'Rejected'
                    : active
                        ? 'Waiting for internal approval'
                        : 'Approved';
            case 2:
                return rejected
                    ? 'Rejected'
                    : active
                        ? 'Waiting for release to payment'
                        : 'Waiting for payment';
            case 3:
                return rejected
                    ? 'Rejected'
                    : active
                        ? 'Pending payment'
                        : 'Paid';
            default:
                return '';
        }
    }

    switch (step) {
        case 0:
            return rejected
                ? 'Rejected'
                : active
                    ? 'Awaiting first approval'
                    : 'Checked';
        case 1:
            return rejected
                ? 'Rejected'
                : active
                    ? 'Awaiting approval'
                    : 'Approved';
        case 2:
            return rejected
                ? 'Rejected'
                : active
                    ? 'Awaiting final approval'
                    : 'Ready';
        case 3:
            return rejected
                ? 'Rejected'
                : active
                    ? 'Awaiting payment'
                    : 'Paid';
        default:
            return '';
    }
};

// ==========================================================================
// Shiny Step Icon
// ==========================================================================

const ShinyStepIcon = (props) => {
    const { icon, index, activeStep, failed } = props;

    let baseColor;

    if (failed >= 0) {
        // Irgendein Step rejected -> dieser rot, alle anderen grau
        if (index === failed) {
            baseColor = 'error.main';
        } else {
            baseColor = 'grey.600';
        }
    } else {
        if (index < activeStep) {
            // Done
            baseColor = '#388e3c';
        } else if (index === activeStep) {
            // Aktueller Step
            baseColor = 'warning.main';
        } else {
            // Future
            baseColor = 'grey.600';
        }
    }

    return (
        <Box
            sx={{
                width: 25,
                height: 25,
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 600,
                color: '#fff',
                // Grundfarbe
                backgroundColor: baseColor,
                backgroundImage: 'none',
                boxShadow: 'none',
            }}
        >
            {icon}
        </Box>
    );
};

// ==========================================================================
// Component
// ==========================================================================

const SupplyInvoiceAcceptanceState = ({
                                          showButtons,
                                          initial,
                                          oldSystem,
                                          paid,
                                          onStepDone,
                                          onStepRejected,
                                          defaultView = true,
                                          labelMode = 'internal',
                                      }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [failed, setFailed] = useState(-1);
    const steps = getSteps(oldSystem, labelMode);

    const isRejectStep = (step) => step === 0 || step === 1 || step === 2;
    const isStepRejected = (step) => failed === step;
    const isActiveStep = (step) => activeStep === step;
    const isDone = (step) => activeStep > step;

    // Initialwerte aus Backend / altem System mappen
    useEffect(() => {
        if (!initial) return;

        // hartes Paid-Shortcut
        if (paid || initial.stepFourPaid || initial.stepThreePaid) {
            setActiveStep(4);
            setFailed(-1);
            return;
        }

        if (oldSystem) {
            // Legacy-Mapping (altes System)
            const nextActive = initial.stepThreeAdminApproved
                ? 4
                : initial.stepTwoAmApproved
                    ? 2
                    : initial.stepOneCorrect
                        ? 1
                        : initial.stepZeroStarted
                            ? 0
                            : 0;

            const failedStep =
                initial.stepTwoAmApproved === false
                    ? 1
                    : initial.stepOneCorrect === false
                        ? 0
                        : -1;

            setActiveStep(nextActive);
            setFailed(failedStep);
        } else {
            // neues Mapping
            const nextActive = initial.stepFourPaid
                ? 4
                : initial.stepThreeReady
                    ? 3
                    : initial.stepTwoApproved
                        ? 2
                        : initial.stepOneUpload
                            ? 1
                            : initial.stepZeroStarted
                                ? 0
                                : 0;

            const failedStep =
                initial.stepThreeReady === false
                    ? 2
                    : initial.stepTwoApproved === false
                        ? 1
                        : initial.stepOneUpload === false
                            ? 0
                            : -1;

            setActiveStep(nextActive);
            setFailed(failedStep);
        }
    }, [initial, oldSystem, paid]);

    const handleReject = useCallback(() => {
        if (!onStepRejected) return;
        setFailed(activeStep);
        onStepRejected(
            oldSystem ? oldStepKeys[activeStep] : stepKeys[activeStep]
        );
    }, [activeStep, onStepRejected, oldSystem]);

    const handleNext = useCallback(() => {
        if (failed === activeStep) {
            setFailed(-1);
        }

        if (onStepDone) {
            onStepDone(
                oldSystem ? oldStepKeys[activeStep] : stepKeys[activeStep]
            );
        }

        // lokal weiterzählen (max. bis steps.length)
        setActiveStep((prev) =>
            prev < steps.length ? prev + 1 : prev
        );
    }, [activeStep, failed, onStepDone, oldSystem, steps.length]);

    // ======================================================================
    // Table-View (kompakte Ansicht mit farbigen Kästchen)
    // ======================================================================

    const renderTableView = () => {
        const getStepColor = (index) => {
            if (failed >= 0) {
                if (index === failed) return 'error.main';
                return 'grey.600';
            }

            if (index < activeStep) return '#388e3c';
            if (index === activeStep) return 'warning.main';
            return 'grey.600';
        };

        return (
            <Box
                sx={{
                    display: 'flex',
                    gap: 0.3,
                    mt: 1,
                }}
            >
                {steps.map((label, index) => (
                    <Tooltip
                        key={index}
                        placement="top"
                        title={getTooltipText(
                            index,
                            isActiveStep(index),
                            isStepRejected(index),
                            labelMode
                        )}
                    >
                        <Box
                            sx={{
                                width: 25,
                                height: 25,
                                borderRadius: '3px',
                                bgcolor: getStepColor(index),
                                color: 'white',
                                fontSize: 14,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'default',
                                transition: 'all 0.15s ease',
                                backgroundImage: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                    opacity: 0.9,
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {index + 1}
                        </Box>
                    </Tooltip>
                ))}
            </Box>
        );
    };

    // ======================================================================
    // Stepper-View (Standard-Ansicht mit shiny Badges)
    // ======================================================================

    const renderStepperView = () => (
        <>
            <Stepper activeStep={activeStep}>
                {steps.map((label, index) => {
                    const rejected = isStepRejected(index);
                    const active = isActiveStep(index);
                    const done = isDone(index);

                    const labelProps = {
                        StepIconComponent: (stepIconProps) => (
                            <ShinyStepIcon
                                {...stepIconProps}
                                index={index}
                                activeStep={activeStep}
                                failed={failed}
                            />
                        ),
                    };
                    if (rejected) {
                        labelProps.error = true;
                    }

                    return (
                        <Step key={label}>
                            <StepLabel {...labelProps}>
                                {getStepContent(
                                    index,
                                    active,
                                    rejected,
                                    done,
                                    oldSystem,
                                    labelMode
                                )}
                            </StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            {showButtons && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    {isRejectStep(activeStep) && !isStepRejected(activeStep) && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleReject}
                            sx={{ mr: 1 }}
                        >
                            Reject
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleNext}
                    >
                        {activeStep === steps.length - 1
                            ? 'Paid?'
                            : 'Approve'}
                    </Button>
                </Box>
            )}
        </>
    );

    // ======================================================================
    // Render
    // ======================================================================

    return (
        <Box sx={{ width: '100%' }}>
            {defaultView ? renderStepperView() : renderTableView()}
        </Box>
    );
};

export default SupplyInvoiceAcceptanceState;
